import {TemplateResult, html, LitElement } from 'lit';
import {
  screenSizeMap,
  ScreenSizeController,
} from '../../controllers/screen-size-controller';
import { customElement, property } from 'lit/decorators.js';
import { processMenuData } from './utility';
import type { MenuNavItem } from './types';
import globalStyles from './styles/outline-nav.global.css?inline';
import encapsulatedStyles from './styles/outline-nav.encapsulated.css?inline';
import { AdoptedStyleSheets } from '../../controllers/adopted-stylesheets';
import './outline-nav-link/outline-nav-link';
import './outline-nav-item/outline-nav-item';
import './outline-nav-flyout/outline-nav-flyout';

/**
 * The element name, reused throughout the codebase
 * @type {string}
 */
const componentName = 'outline-nav';
/**
 * The OutlineNav component
 * @element outline-nav
 */
@customElement('outline-nav')
export class OutlineNav extends LitElement {
  private _seed = Math.floor(Math.random() * 100000).toString();
  screens = new ScreenSizeController(this);

  /**
   * Adopted stylesheets for the component
   *
   * @type {AdoptedStyleSheets}
   */
  adoptedStyleSheets = new AdoptedStyleSheets(this, {
    globalCSS: globalStyles,
    encapsulatedCSS: encapsulatedStyles,
  });
  // screens = new ScreenSizeController(this);

  /**
   * Sets the aria label for the nav.
   */
  @property({ type: String, attribute: 'label' })
  label = 'main menu';

  /**
   * Generated array of MenuNavItem objects to build the menu.
   */
  @property({ type: Array, attribute: 'list-items' })
  listItems: Array<MenuNavItem>;

  /**
   * Unique Id for the nav menu.
   */
  @property({ type: String, reflect: true, attribute: 'id' })
  id = `menu-${this._seed}`;

  /**
   * URL used by setListItemData to fetch the JSON API menu item data.
   */
  @property({ type: String, attribute: 'data-url' })
  dataURL: string;

  firstUpdated() {
    this.setData();    
  }

  render(): TemplateResult {
    const isMobile = window.innerWidth < (screenSizeMap.get('xl') as number);

    return html`<div class="outline-nav">
      <nav aria-label="${this.label}" class="outline-nav--main">
      <ul class="outline-nav--main-list ${isMobile ? 'mobile' : ''}">
          ${this.generateListItemsFromData(isMobile)}
        </ul>
      </nav>
    </div>`;
  }

  /**
   * Defaults to standard use case if no specific URL is passed.
   * @returns menu sub-components.
   */
  setData() {    
    if (!this.dataURL) {      
      this.dataURL = `${window.location.origin}/jsonapi/menu_items/main`;
    }
    return this.setListItemData(this.dataURL);
  }

  /**
   * Fetches data from url.
   * @param url
   * @returns json parsed response or error.
   */
  async fetchMenuData(url: string) {
    try {
      const response = await fetch(url);
      
      return await response.json();
    } catch (error) {
      throw new Error('no data');
    }
  }

  /**
   * Fetches JSON data from URL, and sets this.listItems with processed data.
   * @param url
   */
  async setListItemData(url: string) {
    const fetchResponse = await this.fetchMenuData(url);
    const data = processMenuData(fetchResponse);
    if (data?.length) {
      this.listItems = data;      
    }
  }

  /**
   *
   * @param isMobile
   * @returns <li> with appropriate menu sub-component.
   */
  generateListItemsFromData(isMobile: boolean) {
    return this.listItems?.map((listItem: MenuNavItem) => {
      const hasSubList = Object?.keys(listItem)?.includes('sub') ? true : false;
      if (hasSubList && !isMobile) {
        return html` <li>
          <outline-nav-flyout
            toplevel
            .parentID=${this.id}
            .item=${listItem}
            id=${listItem.id}
          ></outline-nav-flyout>
        </li>`;
      }
      if (hasSubList && isMobile) {
        return html` <li>
          <outline-nav-item
            toplevel
            .parentID=${this.id}
            .item=${listItem}
            id=${listItem.id}
          ></outline-nav-item>
        </li>`;
      } else {
        return html` <li>
          <outline-nav-link
            toplevel
            id=${listItem.id}
            .item=${listItem}
            slot="list"
          ></outline-nav-link>
        </li>`;
      }
    });
  }
}
/**
 * Declare the component in the global HTMLElementTagNameMap
 */
declare global {
  interface HTMLElementTagNameMap {
    [componentName]: OutlineNav;
  }
}
